import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import {
  IconChevronLeft,
  IconCopy,
  IconGlobe,
  IconTelegram,
  IconX,
  IconBolt,
  IconShare,
} from "./icons";
import { useTokenDetail } from "../../lib/useTokenDetail";
import { adaptSlurpToken } from "../../lib/adaptToken";
import { slurpApi, profileCache } from "../../lib/slurpApi";
import {
  quoteBuy,
  quoteSell,
  buyToken,
  sellToken,
  getTokenBalance,
  toRawAmount,
  fromRawAmount,
} from "../../lib/solanaSwap";
import { readPendingFees, claimFees } from "../../lib/solanaFees";
import { slurpGroups } from "../../lib/slurpGroupsApi";
import ImagePicker from "./ImagePicker";
import RulesEditor from "./RulesEditor";
import MinHoldInput from "./MinHoldInput";
import TVChart from "./TVChart";

const BURN_TOOLTIP =
  "Creator pledged buy-back-and-burn — every fee claim swaps SOL into this token and burns the supply forever.";

// -------------------------------------------------------------------------
// Formatting helpers (module-scoped, no hooks)
// -------------------------------------------------------------------------

const fmtUsd = (n) => {
  if (n == null || Number.isNaN(+n)) return "—";
  const v = +n;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};
const fmtPct = (n) => {
  if (n == null) return null;
  const v = +n;
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
};
const fmtCount = (n) => {
  if (n == null) return "—";
  const v = +n;
  if (v >= 1e9) return `${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1).replace(/\.0$/, "")}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return `${v}`;
};
const fmtAge = (s) => {
  if (s == null) return "—";
  const v = +s;
  if (v < 60) return `${Math.floor(v)}s`;
  if (v < 3600) return `${Math.floor(v / 60)}m`;
  if (v < 86400) return `${Math.floor(v / 3600)}h`;
  return `${Math.floor(v / 86400)}d`;
};
const shortAddr = (a, left = 6, right = 4) => (a ? `${a.slice(0, left)}…${a.slice(-right)}` : "—");

const fmtPrice = (v) => {
  if (!(v > 0)) return "—";
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toExponential(2)}`;
};

const hostnameOf = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "");
  } catch { return url; }
};

const handleOf = (url) => {
  if (!url) return "";
  const m = String(url).match(/(?:x\.com|twitter\.com)\/(@?[A-Za-z0-9_]+)/i);
  return m ? `@${m[1].replace(/^@/, "")}` : "";
};

// -------------------------------------------------------------------------
// Chart — SVG area chart, MC on the Y axis (not price — too many zeros for
// readable labels on memecoins). Takes raw price points and the total supply,
// converts to MC, and formats axis labels as $K/$M.
// -------------------------------------------------------------------------

const fmtMc = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};

function PriceChart({ data, direction, supply, launchMcap }) {
  const w = 1000;
  const h = 360;
  const padL = 64;  // wider Y axis gutter for "$5.1K MC"-style labels
  const padR = 80;  // headroom on the right so the line doesn't glue to the edge
  const padT = 20;
  const padB = 24;

  const [hover, setHover] = useState(null);

  // Convert price points → MC values for everything else.
  const mcData = data.map((p) => p * supply);
  const dataMin = Math.min(...mcData);
  const dataMax = Math.max(...mcData);
  const anchor = Number(launchMcap) || (dataMax + dataMin) / 2 || 1;

  // Force the Y range to span AT LEAST ±50% of the launch MC. That way a
  // barely-traded token doesn't zoom in on a $400 swing and look volatile;
  // a pumping token gets shown relative to where it started.
  const minFloor = anchor * 0.5;
  const maxCeiling = anchor * 1.5;
  let yMin = Math.max(0, Math.min(dataMin * 0.9, minFloor));
  let yMax = Math.max(dataMax * 1.1, maxCeiling);
  // If data blows past the "launch ±50%" frame, pad around data instead.
  if (dataMax > maxCeiling * 0.9) {
    yMin = Math.max(0, dataMin * 0.7);
    yMax = dataMax * 1.2;
  }
  const yRange = yMax - yMin || 1;
  const stepX = (w - padL - padR) / Math.max(1, mcData.length - 1);

  const points = mcData.map((v, i) => ({
    x: padL + i * stepX,
    y: padT + (h - padT - padB) * (1 - (v - yMin) / yRange),
    v,
  }));

  // Horizontal reference line at the launch MC — gives the viewer a sense
  // of "where the token started" vs where it is now.
  const anchorY = padT + (h - padT - padB) * (1 - (anchor - yMin) / yRange);

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const fill = `${path} L${points[points.length - 1].x} ${h - padB} L${points[0].x} ${h - padB} Z`;

  const color = direction === "down" ? "var(--pm-down)" : "var(--pm-up)";
  const id = `pm-detail-chart-${Math.abs(mcData.reduce((a, b) => a + Math.round(b), 0))}`;

  const onMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const xPx = ((e.clientX - rect.left) / rect.width) * w;
    const idx = Math.max(0, Math.min(mcData.length - 1, Math.round((xPx - padL) / stepX)));
    setHover({ idx, ...points[idx] });
  };

  const ticks = 4;
  const yLines = Array.from({ length: ticks + 1 }, (_, i) => {
    const frac = i / ticks;
    const y = padT + (h - padT - padB) * frac;
    const v = yMax - yRange * frac;
    return { y, v };
  });

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      style={{ cursor: "crosshair" }}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.24" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {yLines.map((l, i) => (
        <g key={i}>
          <line
            x1={padL}
            x2={w - padR}
            y1={l.y}
            y2={l.y}
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
          <text
            x={padL - 8}
            y={l.y + 3}
            textAnchor="end"
            fontSize="10"
            fontFamily="var(--pm-font-mono)"
            fill="var(--pm-fg-dim)"
          >
            {fmtMc(l.v)}
          </text>
        </g>
      ))}

      {/* Reference line at launch MC (dashed) */}
      {anchor > 0 && anchorY >= padT && anchorY <= h - padB && (
        <g>
          <line
            x1={padL}
            x2={w - padR}
            y1={anchorY}
            y2={anchorY}
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="1"
            strokeDasharray="3 5"
          />
          <text
            x={padL + 6}
            y={anchorY - 4}
            fontSize="9"
            fontFamily="var(--pm-font-mono)"
            fill="rgba(255,255,255,0.45)"
          >
            launch · {fmtMc(anchor)}
          </text>
        </g>
      )}

      <path d={fill} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

      {/* Explicit dots on each data point when data is sparse. Makes the
          chart readable with only 2-3 trades instead of looking like a
          random slope. */}
      {mcData.length <= 8 && points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill={color} />
      ))}

      {hover && (
        <>
          <line
            x1={hover.x}
            x2={hover.x}
            y1={padT}
            y2={h - padB}
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
          <circle cx={hover.x} cy={hover.y} r="4" fill={color} />
          <rect
            x={Math.min(hover.x + 8, w - 90)}
            y={Math.max(padT, hover.y - 20)}
            width="82"
            height="24"
            rx="4"
            fill="var(--pm-surface-2)"
            stroke="var(--pm-border)"
          />
          <text
            x={Math.min(hover.x + 8, w - 90) + 41}
            y={Math.max(padT, hover.y - 20) + 15}
            textAnchor="middle"
            fontSize="11"
            fontFamily="var(--pm-font-mono)"
            fill="var(--pm-fg)"
          >
            {fmtMc(hover.v)}
          </text>
        </>
      )}
    </svg>
  );
}

const timeAgo = (ms) => {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

// -------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------

const DEFAULT_SLIPPAGES = ["1", "2", "5"];

export default function PremiumDetail() {
  const { contractAddress } = useParams();
  const navigate = useNavigate();

  const { connection } = useConnection();
  const wallet = useWallet();

  // Live feed — token row + recent trades, auto-updating via WS.
  const { token: rawToken, trades: rawTrades, isLoading } = useTokenDetail(contractAddress);
  const token = useMemo(() => adaptSlurpToken(rawToken), [rawToken]);


  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState("2");
  const [tab, setTab] = useState("chart");
  const [copied, setCopied] = useState(null); // "ca" | "creator" | null
  const [creatorProfile, setCreatorProfile] = useState(null);

  // Pull the creator's profile so we can show "by @username" + their pic
  // next to the token name. Seed from the in-memory profile cache so
  // jumping to a token whose creator we've already seen renders without
  // a shortAddr → @username flash.
  useEffect(() => {
    const c = rawToken?.creator;
    if (!c) { setCreatorProfile(null); return; }
    const cached = profileCache.get(c);
    if (cached !== undefined) setCreatorProfile(cached);
    let cancelled = false;
    slurpApi.getProfile(c)
      .then((r) => { if (!cancelled) setCreatorProfile(r.profile); })
      .catch(() => { if (!cancelled && cached === undefined) setCreatorProfile(null); });
    return () => { cancelled = true; };
  }, [rawToken?.creator]);

  // Wallet balances (SOL lamports + raw token amount).
  const [solBalance, setSolBalance] = useState(null);      // lamports
  const [tokenBalance, setTokenBalance] = useState(null);  // raw units
  const decimals = Number(rawToken?.decimals ?? 6);

  const refreshBalances = useCallback(async () => {
    if (!wallet.publicKey) {
      setSolBalance(null);
      setTokenBalance(null);
      return;
    }
    // Read SOL and token balances independently so a single RPC blip
    // doesn't blank both. A token-balance throw used to silently render
    // as "0 LEZ" (Sell button thought the wallet was empty) — now we
    // preserve the last-known-good value on failure and log so we can
    // see when the RPC is actually broken.
    connection.getBalance(wallet.publicKey, "confirmed")
      .then(setSolBalance)
      .catch((err) => console.warn("[balances] SOL read failed:", err?.message));
    if (rawToken?.mint) {
      getTokenBalance(connection, wallet.publicKey, rawToken.mint)
        .then(setTokenBalance)
        .catch((err) => console.warn("[balances] token read failed:", err?.message));
    } else {
      setTokenBalance("0");
    }
  }, [connection, wallet.publicKey, rawToken?.mint]);

  useEffect(() => { void refreshBalances(); }, [refreshBalances]);

  // Live quote — debounced, refreshed on amount/side/slippage/pool change.
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    if (!rawToken?.pool) return;
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    const slippageBps = Math.max(10, Math.floor(Number(slippage) * 100));
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const q =
          side === "buy"
            ? await quoteBuy({
                connection,
                poolAddress: rawToken.pool,
                solAmount: amt,
                slippageBps,
              })
            : await quoteSell({
                connection,
                poolAddress: rawToken.pool,
                tokenAmountRaw: toRawAmount(amt, decimals),
                slippageBps,
              });
        if (!cancelled) setQuote(q);
      } catch (err) {
        if (!cancelled) setQuoteError(err?.message || "Quote failed");
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [connection, rawToken?.pool, amount, side, slippage, decimals]);

  // Holders — only fetched when the holders tab is opened. The endpoint
  // now triggers a server-side on-chain reconcile (2 RPC calls per mint
  // per minute), so firing it on every token-page load — even for visitors
  // who never click the holders tab — would be wasted Helius credits.
  const [holders, setHolders] = useState({ rows: [], supplyRaw: "0", decimals: 6, loading: false, loaded: false });
  useEffect(() => {
    if (!contractAddress || tab !== "holders") return;
    let cancelled = false;
    setHolders((h) => ({ ...h, loading: true }));
    slurpApi.getTokenHolders(contractAddress, { limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setHolders({
          rows: res?.holders ?? [],
          supplyRaw: String(res?.supply_raw ?? "0"),
          decimals: Number(res?.decimals ?? 6),
          loading: false,
          loaded: true,
        });
      })
      .catch(() => { if (!cancelled) setHolders({ rows: [], supplyRaw: "0", decimals: 6, loading: false, loaded: true }); });
    return () => { cancelled = true; };
  }, [contractAddress, tab]);

  // Creator fee state — only populated when the connected wallet is the token's creator.
  const isCreator =
    wallet.publicKey && rawToken?.creator && wallet.publicKey.toBase58() === rawToken.creator;
  const [fees, setFees] = useState({ pendingSolRaw: "0", pendingTokenRaw: "0" });
  const [feeClaim, setFeeClaim] = useState({
    state: "idle",
    sigs: [],
    burnSig: null,
    tokenBurned: "0",
    solGained: "0",
    error: null,
  });

  const refreshFees = useCallback(async () => {
    if (!isCreator || !rawToken?.position) return;
    try {
      const res = await readPendingFees({
        connection,
        positionAddress: rawToken.position,
        poolAddress: rawToken.pool,
      });
      setFees({
        pendingSolRaw: res.pendingSolRaw,
        pendingTokenRaw: res.pendingTokenRaw,
      });
    } catch (err) {
      console.warn("[fees] read failed", err);
    }
  }, [isCreator, rawToken?.position, rawToken?.pool, connection]);
  useEffect(() => { void refreshFees(); }, [refreshFees]);

  const claimCreatorFees = useCallback(async () => {
    if (!isCreator || !rawToken?.pool || !rawToken?.position || !rawToken?.position_nft) return;
    setFeeClaim({ state: "confirming", sigs: [], burnSig: null, tokenBurned: "0", solGained: "0", error: null });
    try {
      const res = await claimFees({
        connection,
        wallet,
        poolAddress: rawToken.pool,
        positionAddress: rawToken.position,
        positionNftMint: rawToken.position_nft,
      });
      setFeeClaim({
        state: "success",
        sigs: [res.claimSig],
        burnSig: null,
        tokenBurned: "0",
        solGained: res.solGained,
        error: null,
      });
      void refreshFees();
      void refreshBalances();
    } catch (err) {
      setFeeClaim({
        state: "error",
        sigs: [],
        burnSig: null,
        tokenBurned: "0",
        solGained: "0",
        error: err?.message || "Claim failed",
      });
    }
  }, [isCreator, rawToken?.pool, connection, wallet, refreshFees, refreshBalances]);

  // Swap execution state for the CTA.
  const [txStatus, setTxStatus] = useState({ state: "idle", sig: null, error: null });
  const executeTrade = useCallback(async () => {
    if (!wallet.publicKey) {
      setTxStatus({ state: "error", sig: null, error: "Connect wallet first" });
      return;
    }
    if (!rawToken?.pool || !rawToken?.mint) return;
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return;
    const slippageBps = Math.max(10, Math.floor(Number(slippage) * 100));
    setTxStatus({ state: "confirming", sig: null, error: null });
    try {
      const result =
        side === "buy"
          ? await buyToken({
              connection,
              wallet,
              poolAddress: rawToken.pool,
              tokenMint: rawToken.mint,
              solAmount: amt,
              slippageBps,
            })
          : await sellToken({
              connection,
              wallet,
              poolAddress: rawToken.pool,
              tokenMint: rawToken.mint,
              tokenAmountRaw: toRawAmount(amt, decimals),
              slippageBps,
            });
      setTxStatus({ state: "success", sig: result.signature, error: null });
      setAmount("");
      void refreshBalances();
    } catch (err) {
      setTxStatus({ state: "error", sig: null, error: err?.message || "Swap failed" });
    }
  }, [wallet, connection, rawToken?.pool, rawToken?.mint, side, amount, slippage, decimals, refreshBalances]);

  // Ensure the legacy cream background helper doesn't fight the dark theme here.
  useEffect(() => {
    document.body.classList.add("token-detail-page");
    return () => document.body.classList.remove("token-detail-page");
  }, []);

  const changeDir = token && token.price_change_24h > 0
    ? "up"
    : token && token.price_change_24h < 0
    ? "down"
    : null;

  const price = Number(rawToken?.last_price_usd || 0);

  // Receive amount — real from the DLMM quote. Buy: raw tokens → whole tokens.
  // Sell: raw lamports → SOL.
  const receiveAmount = useMemo(() => {
    if (!quote) return 0;
    if (side === "buy") return fromRawAmount(quote.outAmountRaw, decimals);
    return Number(quote.outAmountRaw) / LAMPORTS_PER_SOL;
  }, [quote, side, decimals]);

  const copy = async () => {
    if (!token?.contractAddress) return;
    try {
      await navigator.clipboard?.writeText(token.contractAddress);
      setCopied("ca");
      setTimeout(() => setCopied(null), 1400);
    } catch {
      /* noop */
    }
  };

  if (isLoading && !token) {
    return (
      <div className="pm-detail">
        <button className="pm-detail__back" onClick={() => navigate("/")}>
          <IconChevronLeft /> Back
        </button>
        <div className="pm-slurp-loader" role="status" aria-live="polite">
          <img
            src="/images/png/slurpface (1).png"
            alt="Slurp"
            style={{ width: 72, height: 72 }}
          />
          <span className="pm-slurp-loader__label">Loading token</span>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="pm-detail">
        <button className="pm-detail__back" onClick={() => navigate("/")}>
          <IconChevronLeft /> Back
        </button>
        <div className="pm-empty">Token not found</div>
      </div>
    );
  }

  return (
    <div className="pm-detail">
      <button className="pm-detail__back" onClick={() => navigate("/")}>
        <IconChevronLeft /> Back
      </button>

      {/* Identity strip: image + name/ticker/desc/meta — stats hang off bottom */}
      <div className="pm-detail__identity">
        <button
          className="pm-detail__share"
          onClick={async (e) => {
            e.stopPropagation();
            const url = window.location.href;
            // Mobile: prefer the native share sheet — it's the expected UX
            // and works without clipboard permissions. Desktop / unsupported
            // browsers fall through to clipboard. Either way, flash "Copied"
            // so the button gives feedback.
            const canShare =
              typeof navigator !== "undefined" &&
              typeof navigator.share === "function";
            try {
              if (canShare) {
                await navigator.share({ title: token?.name || "Slurp", url });
              } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
              } else {
                // Last-resort fallback — old browsers / non-HTTPS contexts.
                const ta = document.createElement("textarea");
                ta.value = url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
              }
              setCopied("share");
              setTimeout(() => setCopied(null), 1400);
            } catch (err) {
              // User cancelled the share sheet — that's normal, don't flash.
              if (err?.name !== "AbortError") {
                console.warn("[share] failed:", err);
              }
            }
          }}
          title="Share token"
          aria-label="Share"
        >
          <IconShare />
          <span>{copied === "share" ? "Copied" : "Share"}</span>
        </button>
        <div className="pm-detail__image">
          {token.image?.url ? (
            <img src={token.image.url} alt={token.name} />
          ) : (
            <div className="pm-detail__image-ph" />
          )}
          <div className="pm-detail__image-name" title={`$${token.symbol}`}>
            ${token.symbol}
          </div>
        </div>
        <div className="pm-detail__id-col">
          <div className="pm-detail__id-head">
            <h1 className="pm-detail__name">{token.name}</h1>
            {rawToken?.creator && (
              <a
                className="pm-detail__byline"
                href={`#/profile/${rawToken.creator}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/profile/${rawToken.creator}`);
                }}
                title={`View creator profile · ${rawToken.creator}`}
              >
                <span className="pm-detail__byline-label">By</span>
                {creatorProfile?.image_url ? (
                  <img src={creatorProfile.image_url} alt="" className="pm-detail__byline-avatar" />
                ) : (
                  <img
                    src="/images/png/slurpface (1).png"
                    alt=""
                    className="pm-detail__byline-avatar pm-detail__byline-avatar--default"
                  />
                )}
                <span className="pm-detail__byline-name">
                  {creatorProfile?.username
                    ? `@${creatorProfile.username}`
                    : shortAddr(rawToken.creator, 4, 4)}
                </span>
              </a>
            )}
            <div className="pm-detail__id-meta">
              {token.website && (
                <a className="pm-detail__link pm-detail__link--icon" href={token.website} target="_blank" rel="noreferrer" aria-label="Website">
                  <IconGlobe />
                </a>
              )}
              {token.telegram && (
                <a className="pm-detail__link pm-detail__link--icon" href={token.telegram} target="_blank" rel="noreferrer" aria-label="Telegram">
                  <IconTelegram />
                </a>
              )}
              {token.twitter && (
                <a className="pm-detail__link pm-detail__link--icon" href={token.twitter} target="_blank" rel="noreferrer" aria-label="Twitter">
                  <IconX />
                </a>
              )}
              {rawToken?.mint && (
                <a
                  className="pm-detail__link pm-detail__link--icon pm-detail__link--dex"
                  href={`https://dexscreener.com/solana/${rawToken.mint}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="DexScreener"
                >
                  <img src="/images/png/dex-screener-seeklogo.png" alt="" />
                </a>
              )}
            </div>
          </div>
          {rawToken?.description && (
            <p className="pm-detail__desc">{rawToken.description}</p>
          )}
        </div>
        <button
          className="pm-detail__chip pm-detail__chip--corner"
          onClick={copy}
          title="Copy contract address"
        >
          <IconCopy />
          <span>{shortAddr(token.contractAddress, 6, 4)}</span>
          {copied === "ca" && <span className="pm-detail__chip-ok">Copied</span>}
        </button>
        {rawToken?.mint && (
          <HolderGroupCTA
            mint={rawToken.mint}
            symbol={rawToken.symbol}
            tokenCreator={rawToken.creator}
            supplyWhole={rawToken.supply_raw && rawToken.decimals != null
              ? Number(rawToken.supply_raw) / 10 ** Number(rawToken.decimals)
              : 0}
          />
        )}
      </div>

      {/* Stats strip — pinned to the bottom of the identity banner */}
      <div className="pm-detail__topstats">
        <div className="pm-topstat">
          <span className="pm-topstat__label">Market Cap</span>
          <span className="pm-topstat__value">{fmtUsd(token.market_cap_usd)}</span>
        </div>
        <div className="pm-topstat">
          <span className="pm-topstat__label">24h Volume</span>
          <span className="pm-topstat__value">{fmtUsd(token.volume_usd_24h)}</span>
        </div>
        <div className="pm-topstat">
          <span className="pm-topstat__label">24h</span>
          <span className={`pm-topstat__value pm-topstat__value--${changeDir === "down" ? "down" : "up"}`}>
            {fmtPct(token.price_change_24h) || "—"}
          </span>
        </div>
        <div className="pm-topstat">
          <span className="pm-topstat__label">Age</span>
          <span className="pm-topstat__value">{fmtAge(token.age_seconds)}</span>
        </div>
        {token.fee_mode === "burn" && (() => {
          const raw = Number(rawToken?.total_burned_raw ?? token.total_burned_raw ?? 0);
          const decimals = Number(rawToken?.decimals ?? token.decimals ?? 6);
          const whole = raw / 10 ** decimals;
          const fmtBurn = (v) => {
            if (!Number.isFinite(v) || v <= 0) return "0";
            if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
            if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
            if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
            return Math.floor(v).toString();
          };
          return (
            <div
              className="pm-topstat pm-topstat--burn"
              title={BURN_TOOLTIP}
              aria-label="Total tokens burnt"
            >
              <span className="pm-topstat__label">Total Burnt</span>
              <span className="pm-topstat__value">{fmtBurn(whole)}</span>
            </div>
          );
        })()}
        {token.fee_mode === "lp_compound" && (() => {
          const usd = Number(rawToken?.lp_added_usd ?? token.lp_added_usd ?? 0);
          const fmtLp = (v) => {
            if (!Number.isFinite(v) || v <= 0) return "$0";
            if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
            if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
            if (v >= 1) return `$${v.toFixed(2)}`;
            return `$${v.toFixed(4)}`;
          };
          return (
            <div
              className="pm-topstat pm-topstat--lp"
              title="Creator's fees auto-compound back into the pool. This is the running USD value the worker has added so far."
              aria-label="Fees added to LP"
            >
              <span className="pm-topstat__label">Fees added to LP</span>
              <span className="pm-topstat__value">{fmtLp(usd)}</span>
            </div>
          );
        })()}
        {token.dex_paid_at && (
          <div
            className="pm-topstat pm-topstat--dex"
            title="Creator paid for the DexScreener profile (uploaded header image + extra info)"
            aria-label="DEX profile paid"
          >
            <span className="pm-topstat__label">DEX</span>
            <span className="pm-topstat__value">PAID</span>
          </div>
        )}
      </div>

      {/* 2-col main: chart column on left, buy panel on right */}
      <div className="pm-detail__grid">
        <div>
          {/* Unified panel: tabs at top, content (chart/trades/holders/info)
              swaps below. One continuous card. */}
          <div className="pm-tabs">
            <button data-active={tab === "chart" ? "true" : "false"} onClick={() => setTab("chart")}>
              Chart
            </button>
            <button data-active={tab === "trades" ? "true" : "false"} onClick={() => setTab("trades")}>
              Trades
            </button>
            <button data-active={tab === "holders" ? "true" : "false"} onClick={() => setTab("holders")}>
              Holders
            </button>
          </div>

          {tab === "chart" && (
            <div className="pm-tab-panel pm-tab-panel--chart">
              <TVChart
                mint={rawToken?.mint}
                pool={rawToken?.pool}
                tokenSymbol={token.symbol}
                supply={
                  Number(rawToken?.supply_raw || 0) / 10 ** Number(rawToken?.decimals ?? 6) ||
                  1_000_000_000
                }
                height={500}
              />
            </div>
          )}

          {tab === "trades" && (
            <div className="pm-table-wrap">
            <table className="pm-table pm-table--trades">
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "26%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Side</th>
                  <th className="right">USD</th>
                  <th className="right">SOL</th>
                  <th className="right">Tokens</th>
                  <th>Maker</th>
                </tr>
              </thead>
              <tbody>
                {(rawTrades || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="dim" style={{ textAlign: "center", padding: 24 }}>
                      No trades yet
                    </td>
                  </tr>
                ) : (
                  rawTrades.map((t) => {
                    const solHuman = Number(t.sol_raw || 0) / 1e9;
                    const tokHuman = Number(t.token_raw || 0) / 10 ** Number(rawToken?.decimals ?? 6);
                    const usdHuman = solHuman * Number(t.sol_usd || 0);
                    const isDev = rawToken?.creator && t.trader === rawToken.creator;
                    return (
                      <tr
                        key={t.signature}
                        className="pm-table__row pm-table__row--link"
                        onClick={() => window.open(`https://solscan.io/tx/${t.signature}`, "_blank", "noopener,noreferrer")}
                        title="View transaction on Solscan"
                      >
                        <td className="dim">{timeAgo(new Date(t.ts).getTime())}</td>
                        <td className={t.side === "buy" ? "up" : "down"}>
                          {t.side === "buy" ? "Buy" : "Sell"}
                        </td>
                        <td className="right">{usdHuman > 0 ? (usdHuman >= 1000 ? fmtUsd(usdHuman) : `$${usdHuman.toFixed(2)}`) : "—"}</td>
                        <td className="right">{solHuman.toFixed(4)}</td>
                        <td className="right">{fmtCount(Math.floor(tokHuman))}</td>
                        <td className="dim">
                          <span className="pm-table__maker">
                            <a
                              href={`https://solscan.io/account/${t.trader}`}
                              target="_blank"
                              rel="noreferrer"
                              className="pm-table__addr"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t.trader ? t.trader.slice(-4) : "—"}
                            </a>
                            {isDev && <span className="pm-dev-sticker">DEV</span>}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {/* Same 90-day retention applies to per-token trades. Most
                memecoins won't accumulate 90+ days of trades anyway, but
                the note is here for the long-tail tokens that do. */}
            {(rawTrades || []).length > 0 && (
              <div className="pm-table-foot">
                Showing last 90 days ·{" "}
                <a
                  href={`https://solscan.io/token/${contractAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  full history on Solscan
                </a>
              </div>
            )}
            </div>
          )}

          {tab === "holders" && (
            <div className="pm-table-wrap">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Wallet</th>
                  <th className="right">Balance</th>
                  <th className="right">% supply</th>
                </tr>
              </thead>
              <tbody>
                {holders.loading && holders.rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                      <div className="pm-slurp-loader" role="status" aria-live="polite">
                        <img
                          src="/images/png/slurpface (1).png"
                          alt="Slurp"
                          style={{ width: 56, height: 56 }}
                        />
                        <span className="pm-slurp-loader__label">Loading top 20 holders</span>
                      </div>
                    </td>
                  </tr>
                ) : holders.rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="dim" style={{ textAlign: "center", padding: 24 }}>
                      No holders yet
                    </td>
                  </tr>
                ) : (
                  holders.rows.map((h, i) => {
                    const whole = fromRawAmount(h.balance_raw, holders.decimals);
                    const supplyWhole = fromRawAmount(holders.supplyRaw, holders.decimals);
                    const pct = supplyWhole > 0 ? (whole / supplyWhole) * 100 : 0;
                    const isDev = rawToken?.creator && h.owner === rawToken.creator;
                    return (
                      <tr key={h.owner}>
                        <td className="dim">#{i + 1}</td>
                        <td>
                          <span className="pm-table__maker">
                            <a
                              href={`https://solscan.io/account/${h.owner}`}
                              target="_blank"
                              rel="noreferrer"
                              className="pm-table__addr"
                            >
                              {shortAddr(h.owner)}
                            </a>
                            {isDev && <span className="pm-dev-sticker">DEV</span>}
                          </span>
                        </td>
                        <td className="right">{fmtCount(whole)}</td>
                        <td className="right">{pct.toFixed(2)}%</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          )}

        </div>

        {/* Trade panel */}
        <aside>
          <div className="pm-trade">
            <div className="pm-trade__toggle">
              <button
                data-side="buy"
                data-active={side === "buy" ? "true" : "false"}
                onClick={() => setSide("buy")}
              >
                Buy
              </button>
              <button
                data-side="sell"
                data-active={side === "sell" ? "true" : "false"}
                onClick={() => setSide("sell")}
              >
                Sell
              </button>
            </div>

            <div className="pm-trade__amount">
              <div className="pm-trade__amount-label">
                <span>You pay</span>
                <span>
                  Balance{" "}
                  {side === "buy"
                    ? solBalance != null
                      ? `${(solBalance / LAMPORTS_PER_SOL).toFixed(4)} SOL`
                      : "—"
                    : tokenBalance != null
                      ? `${fmtCount(fromRawAmount(tokenBalance, decimals))} ${token.symbol}`
                      : "—"}
                </span>
              </div>
              <div className="pm-trade__amount-row">
                <input
                  className="pm-trade__amount-input"
                  inputMode="decimal"
                  placeholder="0.0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                />
                <span className="pm-trade__unit">{side === "buy" ? "SOL" : token.symbol}</span>
              </div>
            </div>

            <div className="pm-quick-amounts">
              {side === "buy"
                ? ["0.05", "0.1", "0.5", "1"].map((v) => (
                    <button key={v} onClick={() => setAmount(v)}>
                      {v} SOL
                    </button>
                  ))
                : [25, 50, 75, 100].map((pct) => (
                    <button
                      key={pct}
                      disabled={!tokenBalance || tokenBalance === "0"}
                      onClick={() => {
                        if (!tokenBalance) return;
                        const whole = fromRawAmount(tokenBalance, decimals);
                        setAmount(((whole * pct) / 100).toString());
                      }}
                    >
                      {pct === 100 ? "MAX" : `${pct}%`}
                    </button>
                  ))}
            </div>

            <div className="pm-trade__meta">
              <div className="pm-trade__meta-row">
                <span>You receive</span>
                <strong>
                  {quote && receiveAmount > 0
                    ? `${fmtCount(receiveAmount)} ${side === "buy" ? token.symbol : "SOL"}`
                    : quoteError
                    ? "Quote failed"
                    : amount
                    ? "Quoting…"
                    : "—"}
                </strong>
              </div>
              <div className="pm-trade__meta-row">
                <span>Min received</span>
                <strong>
                  {quote
                    ? side === "buy"
                      ? `${fmtCount(fromRawAmount(quote.minOutRaw, decimals))} ${token.symbol}`
                      : `${(Number(quote.minOutRaw) / LAMPORTS_PER_SOL).toFixed(6)} SOL`
                    : "—"}
                </strong>
              </div>
              <div className="pm-trade__meta-row">
                <span>Price impact</span>
                <strong>{quote ? `${quote.priceImpactPct.toFixed(2)}%` : "—"}</strong>
              </div>
              <div className="pm-trade__meta-row" style={{ alignItems: "center" }}>
                <span>Slippage</span>
                <span style={{ display: "inline-flex", gap: 4 }}>
                  {DEFAULT_SLIPPAGES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSlippage(s)}
                      style={{
                        padding: "2px 8px",
                        fontFamily: "var(--pm-font-mono)",
                        fontSize: 11,
                        color: slippage === s ? "var(--pm-accent)" : "var(--pm-fg-muted)",
                        background: slippage === s ? "rgba(215,181,109,0.06)" : "var(--pm-surface-2)",
                        border: `1px solid ${slippage === s ? "rgba(215,181,109,0.35)" : "var(--pm-border)"}`,
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      {s}%
                    </button>
                  ))}
                </span>
              </div>
            </div>

            <button
              className={`pm-trade__cta ${side === "sell" ? "pm-trade__cta--sell" : ""}`}
              disabled={
                !wallet.publicKey ||
                txStatus.state === "confirming" ||
                !amount ||
                parseFloat(amount) <= 0
              }
              onClick={executeTrade}
            >
              <IconBolt />
              {!wallet.publicKey
                ? "Connect wallet"
                : txStatus.state === "confirming"
                ? "Confirming…"
                : side === "buy"
                ? `Buy ${token.symbol}`
                : `Sell ${token.symbol}`}
            </button>

            {txStatus.state === "success" && txStatus.sig && (
              <div className="pm-trade__status pm-trade__status--success">
                Done.{" "}
                <a
                  href={`https://solscan.io/tx/${txStatus.sig}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Solscan ↗
                </a>
              </div>
            )}
            {txStatus.state === "error" && txStatus.error && (
              <div className="pm-trade__status pm-trade__status--error">
                {txStatus.error}
                {/* Surface the most common fix when the chain rejects on
                    slippage. The on-chain error string for a failed
                    minimumAmountOut check shows up as "ExceededSlippage"
                    or just "slippage". */}
                {/slippage|ExceededSlippage|exceededslippage/i.test(txStatus.error) && (
                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                    Try a higher slippage (5%+) — pool may be moving fast.
                  </div>
                )}
              </div>
            )}
          </div>

        </aside>
      </div>
    </div>
  );
}

/**
 * Sticker pinned to the bottom-right of the token's identity banner.
 * Three states:
 *   - group exists  → "Holder Group · Nk members" link, navigates to /groups/:mint
 *   - no group, viewer is creator → "+ Create Holder Group" opens modal
 *   - no group, viewer is anyone else → nothing rendered
 */
function HolderGroupCTA({ mint, symbol, tokenCreator, supplyWhole }) {
  const navigate = useNavigate();
  const wallet = useWallet();
  const [group, setGroup] = useState(null);
  const [checked, setChecked] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChecked(false);
    setGroup(null);
    slurpGroups.get(mint)
      .then((r) => { if (!cancelled) { setGroup(r.group); setChecked(true); } })
      .catch(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, [mint]);

  const isCreator = wallet?.publicKey && tokenCreator && wallet.publicKey.toBase58() === tokenCreator;

  if (!checked) return null;

  if (group) {
    return (
      <button
        type="button"
        className="pm-detail__group-cta pm-detail__group-cta--open"
        onClick={() => navigate(`/groups/${mint}`)}
      >
        <span className="pm-detail__group-cta-label">HOLDER GROUP</span>
        <span className="pm-detail__group-cta-count">
          {Number(group.member_count || 0).toLocaleString()} {Number(group.member_count) === 1 ? "member" : "members"}
        </span>
      </button>
    );
  }

  if (!isCreator) return null;

  return (
    <>
      <button
        type="button"
        className="pm-detail__group-cta pm-detail__group-cta--create"
        onClick={() => setShowCreate(true)}
      >
        + CREATE HOLDER GROUP
      </button>
      {showCreate && (
        <CreateGroupModal
          mint={mint}
          symbol={symbol}
          wallet={wallet}
          supplyWhole={supplyWhole}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            // Drop the user straight into their new group so they can
            // see the empty state and post their first thread.
            navigate(`/groups/${mint}`);
          }}
        />
      )}
    </>
  );
}

function CreateGroupModal({ mint, symbol, wallet, supplyWhole, onClose, onCreated }) {
  const [description, setDescription] = useState("");
  const [rules, setRules] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [minTokens, setMinTokens] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!wallet?.publicKey || !wallet?.signMessage) return;
    if (!agreed) { alert("Please agree to the moderation terms before creating."); return; }
    setBusy(true);
    try {
      await slurpGroups.create({
        mint,
        wallet: wallet.publicKey,
        signMessage: wallet.signMessage,
        banner_url: bannerUrl.trim() || null,
        description: description.trim() || null,
        min_tokens_to_join: Math.max(0, Number(minTokens) || 0),
      });
      // Rules are created/updated via PATCH since the create endpoint
      // doesn't take them. Fire-and-forget — failure here just means
      // the rules aren't saved on first creation; user can edit later.
      const cleanedRules = rules.split("\n").map((s) => s.trim()).filter(Boolean).join("\n");
      if (cleanedRules) {
        slurpGroups.update({
          mint,
          wallet: wallet.publicKey,
          signMessage: wallet.signMessage,
          rules: cleanedRules,
        }).catch(() => {});
      }
      onCreated();
    } catch (err) {
      alert(err?.message || "Couldn't create group");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pm-modal__bg" onClick={onClose}>
      <div className="pm-modal pm-group__create-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pm-modal__close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 3 L11 11 M11 3 L3 11"/></svg>
        </button>
        <h2 className="pm-modal__title">Create ${symbol} Holder Group</h2>
        <p className="pm-group__create-blurb">A token-gated forum for your holders. You'll be the creator with full admin control.</p>
        <form onSubmit={submit} className="pm-group__create-form">
          <label className="pm-group__create-label">
            Description (one sentence, 30 chars max)
            <input
              type="text"
              className="pm-group__create-input"
              placeholder="One sentence about your group"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={30}
            />
            <span className="pm-group__create-hint">{description.length}/30</span>
          </label>
          <label className="pm-group__create-label">
            Group rules (optional)
            <RulesEditor value={rules} onChange={setRules} disabled={busy} />
            <span className="pm-group__create-hint">Each line is a separate rule. Edit anytime.</span>
          </label>
          <label className="pm-group__create-label">
            Banner image (optional)
            <ImagePicker value={bannerUrl} onChange={setBannerUrl} prefix="groups/banners" disabled={busy} />
          </label>
          <label className="pm-group__create-label">
            Minimum ${symbol} to join
            <MinHoldInput
              value={minTokens}
              onChange={setMinTokens}
              symbol={symbol}
              supplyWhole={supplyWhole}
              disabled={busy}
            />
          </label>
          <label className="pm-group__tos">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I understand I'm responsible for moderating this group. Slurp does
              not endorse or take responsibility for content posted by members.
              Groups containing illegal content, hate speech, or other material
              that violates the platform terms will be removed by Slurp without
              notice.
            </span>
          </label>
          <div className="pm-group__create-actions">
            <button type="button" className="pm-group__sticker pm-group__sticker--cream" onClick={onClose}>
              cancel
            </button>
            <button
              type="submit"
              className="pm-group__sticker pm-group__sticker--accent"
              disabled={busy || !agreed}
            >
              {busy ? "creating…" : "create group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
